import { DialogClose } from "@radix-ui/react-dialog";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import {
  ApiError,
  Auth0AccountRole,
  InvitesService,
  FormSummary,
  InviteType,
  TutorsService,
} from "client/openapi";
import { DialogAction, DialogActions, DialogContent } from "components/Dialog";
import { useContext, useEffect, useState } from "react";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import "./index.css";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { ButtonColor } from "components/Button";
import SendFormControls, {
  FormChoiceState,
  NO_FORM,
} from "components/Forms/SendFormControls";
import { sendChosenForm } from "components/Forms/formsApi";
import SendTemplateControls, {
  NO_TEMPLATE,
  TemplateChoiceState,
} from "components/MessageTemplates/SendTemplateControls";
import { sendChosenTemplate } from "components/MessageTemplates/templatesApi";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";

const emptyTutor = {
  email: "",
  first_name: "",
  last_name: "",
};

function InviteTutorDialog({
  fetchTutors,
  currentPage,
  initialEmail = "",
  initialPhone = "",
}: {
  fetchTutors: (page: number) => Promise<void>;
  currentPage: number;
  /** Filled in (an unknown sender being added from Communication). */
  initialEmail?: string;
  initialPhone?: string;
}) {
  const { currently_selected_organization, account } = useContext(
    OrgRolesAndAccountContext
  );

  const [newTutor, setNewTutor] = useState({
    ...emptyTutor,
    email: initialEmail,
  });
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();
  const [formChoice, setFormChoice] = useState<FormChoiceState>(NO_FORM);
  const [templateChoice, setTemplateChoice] =
    useState<TemplateChoiceState>(NO_TEMPLATE);

  const isInviteValid = () => {
    return (
      newTutor.first_name.trim() !== "" &&
      newTutor.last_name.trim() !== "" &&
      newTutor.email.includes("@") &&
      newTutor.email.includes(".") &&
      (phoneNumber === "" || isPhoneValid)
    );
  };

  function inviteTutor() {
    setStatus(PageStatus.LOADING);

    InvitesService.inviteUser({
      requestBody: {
        first_name: newTutor.first_name,
        last_name: newTutor.last_name,
        phone_number: phoneNumber || undefined,
        email: newTutor.email,
        org_invited_by: currently_selected_organization as number,
        acct_invited_by: account?.reference_id as string,
        type: InviteType.TUTOR,
      },
      role: Auth0AccountRole.ORG_TUTOR,
      createEntity: true,
      createAccount: true,
    })
      .then((response) => {
        setStatus(PageStatus.SUCCESS);
        Notifications.success(`Tutor invited!`);
        fetchTutors(currentPage);
        sendChosenForm({
          orgId: currently_selected_organization as number,
          ...formChoice,
          personType: Auth0AccountRole.ORG_TUTOR,
          personId: response?.[1]?.id,
        });
        sendChosenTemplate({
          ...templateChoice,
          personType: Auth0AccountRole.ORG_TUTOR,
          personId: response?.[1]?.id,
        });
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError(e);
      });
  }

  function handleUpdate(ev) {
    const value = ev.target.value;

    setNewTutor({
      ...newTutor,
      [ev.target.name]: value,
    });
  }

  function handleSubmit() {
    return async (ev) => {
      ev.preventDefault();
      await inviteTutor();
    };
  }

  function clearForm() {
    setNewTutor(emptyTutor);
    setStatus(undefined);
    setFormChoice(NO_FORM);
    setTemplateChoice(NO_TEMPLATE);
  }

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <>
      <DialogContent className="dialog-content" onClose={clearForm}>
        <div className="invite-dialog">
          <h2 className="invite-dialog--title">Add a Tutor</h2>

          {status === PageStatus.SUCCESS ? (
            <>
              <p className="mb-4">
                <b>
                  {newTutor.first_name} {newTutor.last_name}
                </b>{" "}
                has been successfully invited!
              </p>

              <DialogActions>
                <DialogAction
                  primary={true}
                  color={ButtonColor.SKYBLUE}
                  onClick={clearForm}
                >
                  Invite Another <ArrowRightIcon />
                </DialogAction>

                <DialogClose asChild>
                  <DialogAction color={ButtonColor.SKYBLUE} onClick={clearForm}>
                    Close
                  </DialogAction>
                </DialogClose>
              </DialogActions>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit()}>
                <label htmlFor="first_name" className="input-label">
                  First Name
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    className="input"
                    placeholder="Amy"
                    value={newTutor.first_name}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required
                  />
                </label>

                <label htmlFor="last_name" className="mt-3 input-label">
                  Last Name
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    className="input"
                    placeholder="Smith"
                    value={newTutor.last_name}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required
                  />
                </label>

                <label htmlFor="email" className="mt-3 input-label">
                  Email Address
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="input"
                    placeholder="amy@example.com"
                    value={newTutor.email}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required
                  />
                </label>

                <label htmlFor="phone_number" className="mt-3 input-label">
                  Phone Number (optional)
                  <div className="mt-2">
                    <IntlTelInput
                      initialValue={initialPhone}
                      inputProps={{
                        className: "input mt-2",
                        name: "tutor_phone_number",
                      }}
                      onChangeValidity={setIsPhoneValid}
                      onChangeNumber={(number) => setPhoneNumber(number)}
                      initOptions={{
                        loadUtils: () =>
                          import("intl-tel-input/build/js/utils.js"),
                        strictMode: true,
                        autoPlaceholder: "aggressive",
                        placeholderNumberType: "MOBILE",
                        countryOrder: ["us", "gb", "ca"],
                        initialCountry: "auto",
                        geoIpLookup: (success, failure) => {
                          fetch("https://ipapi.co/json/")
                            .then((res) => res.json())
                            .then((data) => success(data.country_code))
                            .catch(() => failure());
                        },
                        separateDialCode: true,
                        nationalMode: true,
                      }}
                    />
                  </div>
                </label>

                <SendFormControls
                  orgId={currently_selected_organization}
                  audience={FormSummary.audience.TUTORS}
                  value={formChoice}
                  onChange={setFormChoice}
                  disabled={status === PageStatus.LOADING}
                />
                <SendTemplateControls
                  orgId={currently_selected_organization}
                  value={templateChoice}
                  onChange={setTemplateChoice}
                  disabled={status === PageStatus.LOADING}
                />

                <DialogActions>
                  <DialogAction
                    primary={true}
                    color={
                      !isInviteValid() ? ButtonColor.GRAY : ButtonColor.SKYBLUE
                    }
                    type="submit"
                    disabled={status === PageStatus.LOADING || !isInviteValid()}
                  >
                    {status === PageStatus.LOADING ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : (
                      <>
                        Invite <ArrowRightIcon />
                      </>
                    )}
                  </DialogAction>

                  <DialogClose asChild>
                    <DialogAction
                      color={ButtonColor.SKYBLUE}
                      onClick={clearForm}
                    >
                      Cancel
                    </DialogAction>
                  </DialogClose>
                </DialogActions>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </>
  );
}

export default InviteTutorDialog;
